import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { FrVrfButton, FrVrfFooter } from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation, createPath } from "react-router-dom";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages";
import { useGetGeneralCodes } from "../../services/data-list";
import { EvntGrp } from "../../util/general-code/enums/evnt-grp";
import {
  EnvTypeCd,
  InDefCommonFlagType,
  ReviewActionType,
  TcktStsTypeCpntDsgn,
  TcktStsTypeCpntPrdDplymnt,
  TcktStsTypeCpntPrjPrgrs,
  TcktStsTypeCpntStgDplymnt,
  TcktType,
} from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import { EdhPrtclInfo, EditCell, SpreadsheetState, usePageState } from "./";
import {
  CpntAttr,
  GetEdhPrtclAttribute,
  EdhPrtclInfo as GetEdhPrtclInfo,
  useGetComponent,
  useUpdateComponentAttributes,
} from "./../../services/component";
import {
  UpdateCpntTckt,
  useUpdateComponentAttributeTicketStatuses,
} from "./../../services/tickets";
import {
  PAGE_MODE,
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  requestCdTypes,
} from "./EdhProtocolAttributePageConstant";
import {
  ErrorResponse,
  INIT_ERROR_RESULT_DATA,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";
import _ from "lodash";
export const EdhProtocolAttributePageContent = () => {
  const { showConfirmationDialog, showToast, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const navigate = useNavigate();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const [getGeneralCodesResponse, sendGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getComponentResponse, sendGetComponentRequest] = useGetComponent();
  const [
    updateComponentAttributesResponse,
    sendUpdateComponentAttributesRequest,
  ] = useUpdateComponentAttributes();
  const [
    updateComponentAttributeTicketStatusesResponse,
    sendUpdateComponentAttributeTicketStatusesRequest,
  ] = useUpdateComponentAttributeTicketStatuses();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const params = {
    dipByRgn: queryParameter.get("dipByRgn") ?? "",
    rootCpntId: queryParameter.get("rootCpntId") ?? "",
    cpntId: queryParameter.get("cpntId") ?? "",
    godFlg: queryParameter.get("godFlg") ?? "",
    mode: queryParameter.get("mode") ?? "",
  };

  const mode: string = params.mode;
  const dipByRgn: string = params.dipByRgn;
  const cpntId: string = params.cpntId;
  const rootCpntId: string = params.rootCpntId;

  const EVENT_COMPONENT_PJ = "componentProject";
  const EVENT_COMPONENT_PJ_NEW_TAB_ICON = "componentProject_newTabIcon";
  const EVENT_COMMENT = "cmnt";
  const EVENT_ACTION = "action";
  const EVENT_STG_DESIGN_VALUE = "stgDesignValue";
  const EVENT_PRD_DESIGN_VALUE = "prdDesignValue";
  const EVENT_SELECTED_RQUEST = "isRequest";

  const templateFileUrl = useMemo(() => {
    return mode === PAGE_MODE.REVIEW
      ? TEMPLATE_FILE_URL.REVIEW
      : TEMPLATE_FILE_URL.APPROVAL;
  }, [mode]);

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const getEditCells = (edhPrtclInfo: EdhPrtclInfo[]) => {
    let set = new Set();
    let res: EditCell = {};
    const addEditcells = (key: string, initValue: string) => {
      if (!set.has(key)) {
        set.add(key);
        res[key] = {
          isDirty: false,
          isError: false,
          initialValue: initValue,
          currentValue: "",
        };
      }
    };
    edhPrtclInfo.forEach((edhPrtcl: EdhPrtclInfo) => {
      addEditcells(
        "comment" + "-" + edhPrtcl.idx,
        edhPrtcl.edhPrtclAttributeList.cmnt,
      );
      addEditcells("action" + "-" + edhPrtcl.idx, edhPrtcl.action);
      edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList.forEach(
        (attribute: GetEdhPrtclAttribute) => {
          addEditcells(
            "stgDesignValue" +
              "-" +
              edhPrtcl.idx +
              "-" +
              attribute.edhPrtclAttributenum,
            attribute.stgDesignValue,
          );
          addEditcells(
            "prdDesignValue" +
              "-" +
              edhPrtcl.idx +
              "-" +
              attribute.edhPrtclAttributenum,
            attribute.prdDesignValue,
          );
        },
      );
    });
    return res;
  };

  const spreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    let edhPrtcl: EdhPrtclInfo[] = [];
    if (mode === PAGE_MODE.REQUEST_APPROVAL) {
      edhPrtcl = pageState.edhPrtclInfo.filter(
        (e) =>
          e.edhPrtclAttributeList.designStatus === TcktStsTypeCpntDsgn.DESIGN ||
          e.edhPrtclAttributeList.designStatus ===
            TcktStsTypeCpntDsgn.DESIGN_REVIEW,
      );
    } else {
      edhPrtcl = pageState.edhPrtclInfo.filter(
        (e) =>
          e.edhPrtclAttributeList.designStatus ===
            TcktStsTypeCpntDsgn.DESIGN_REVIEW ||
          e.edhPrtclAttributeList.designStatus ===
            TcktStsTypeCpntDsgn.DESIGN_APPROVED,
      );
    }
    const generalCode = pageState.generalCode;
    return {
      edhPrtclInfo: edhPrtcl.map((e, i) => ({ ...e, idx: i })),
      generalCode: generalCode,
      editCell: getEditCells(edhPrtcl.map((e, i) => ({ ...e, idx: i }))),
    };
  }, [pageState.edhPrtclInfo, pageState.generalCode]);

  const onInitialDisplayed = () => {
    resetUserError();
    sendGeneralCodesRequest({ cdTypes: requestCdTypes });
    sendGetComponentRequest({
      dipByRgn: dipByRgn,
      rootComponentId: rootCpntId,
      componentId: cpntId,
      godFlg: InDefCommonFlagType.NO,
    });
    setAppState((prevState) => ({
      ...prevState,
      title: "EDH Protocol Attribute",
    }));
  };

  const onGetGeneralCodesResponse = () => {
    let reviewActionType = [] as Option[];
    let reviewingActionType = [] as Option[];
    let designCompletedActionType = [] as Option[];
    let inDefCommonFlagType = [] as Option[];
    const GeneralCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];
    if (GeneralCode.length === 0) {
      return;
    }
    GeneralCode.map((e) => {
      switch (e.cdType) {
        case REQUEST_CD_TYPES.REVIEW_ACTION_TYPE:
          reviewActionType = e.cdList;
          reviewActionType.map((e) => {
            if (
              e.value === ReviewActionType.APPROVE ||
              e.value === ReviewActionType.REJECT
            )
              reviewingActionType.push(e);
            if (e.value === ReviewActionType.DISMISS_APPROVAL)
              designCompletedActionType.push(e);
          });
          break;

        case REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE:
          inDefCommonFlagType = e.cdList;
          break;
      }
    });
    setPageState((prevState) => ({
      ...prevState,
      generalCode: {
        reviewingActionType: reviewingActionType,
        designCompletedActionType: designCompletedActionType,
        inDefCommonFlagType: inDefCommonFlagType,
      },
    }));
  };

  const isEditableAttr = (
    targetAttr: GetEdhPrtclAttribute,
    targetIdx: number,
    targetPrtcl: GetEdhPrtclInfo,
    prtcls: GetEdhPrtclInfo[],
  ): boolean => {
    // If this protocol has no attribte, the properties of
    //  edhPrtclAttributeList[0] is empty string ""
    if (!targetPrtcl.edhPrtclAttributeList[0].key) {
      return false;
    }
    return !prtcls.some(
      (p, idx) =>
        targetIdx !== idx &&
        p.designStatus === TcktStsTypeCpntDsgn.DESIGN_APPROVED &&
        ((p.stgOnlyFlg === InDefCommonFlagType.YES &&
          p.stgDeployStatus !== TcktStsTypeCpntStgDplymnt.DEPLOYED) ||
          p.prdDeployStatus !== TcktStsTypeCpntPrdDplymnt.DEPLOYED) &&
        p.edhPrtclAttributeList.some(
          (a) => a.edhPrtclAttributenum === targetAttr.edhPrtclAttributenum,
        ),
    );
  };

  const onGetComponentResponse = () => {
    if (!getComponentResponse) return;
    const targetEdhPrtclInfoList = getComponentResponse.edhPrtclInfoList;

    const edhPrtclInfoList: EdhPrtclInfo[] = targetEdhPrtclInfoList.map(
      (e, idx) => ({
        ...INIT_ERROR_RESULT_DATA,
        idx: idx,
        editedAttributeListInfo: [],
        isRequest: false,
        isCommentEdit: false,
        action: "",
        edhPrtclAttributeList: {
          ...e,
          edhPrtclAttributeList: e.edhPrtclAttributeList.map((ee) => ({
            ...ee,
            isEditableAttr: isEditableAttr(ee, idx, e, targetEdhPrtclInfoList),
          })),
        },
      }),
    );

    setPageState((prevState) => ({
      ...prevState,
      edhPrtclInfo: edhPrtclInfoList,
    }));
  };

  const onUpdateComponentAttributeResponse = () => {
    if (!updateComponentAttributesResponse) return;
    if (Array.isArray(updateComponentAttributesResponse)) {
      if (!spreadsheetState) return;

      // copy and mapping error response to spreadsheet state.
      const currentEdhPrtcl: EdhPrtclInfo[] = spreadsheetState.edhPrtclInfo;
      const emptyColumnList = [{ label: "", value: "" }];
      const newSpreadsheetRecordData: EdhPrtclInfo[] =
        mappingErrorResponseToSpreadsheet(
          updateComponentAttributesResponse,
          currentEdhPrtcl,
          emptyColumnList,
        );

      // copy and set error flag to to spreadsheet state.
      let copyEditCell: EditCell = JSON.parse(
        JSON.stringify(spreadsheetState.editCell),
      );
      updateComponentAttributesResponse.forEach((error: ErrorResponse) => {
        error.errorColumnList.forEach((errorColumn: string) => {
          const cell = copyEditCell[errorColumn];
          cell.isError = true;
        });
      });

      setSpreadsheetState({
        ...spreadsheetState,
        edhPrtclInfo: newSpreadsheetRecordData,
        editCell: copyEditCell,
      });
    } else {
      if (updateComponentAttributesResponse) {
        showToast({
          design: "completion",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
        });
        sendGetComponentRequest({
          dipByRgn: dipByRgn,
          rootComponentId: rootCpntId,
          componentId: cpntId,
          godFlg: InDefCommonFlagType.NO,
        });
        setPageState((prevState) => ({
          ...prevState,
          isSaved: true,
        }));
      }
    }
  };

  const onUpdateComponentAttributeTicketStatusesResponse = () => {
    if (!updateComponentAttributeTicketStatusesResponse) return;
    if (Array.isArray(updateComponentAttributeTicketStatusesResponse)) {
      if (!spreadsheetState) {
        return;
      }
      // copy and mapping error response to spreadsheet state.
      const currentEdhPrtcl: EdhPrtclInfo[] = spreadsheetState.edhPrtclInfo;
      const emptyColumnList = [{ label: "Comment", value: "cmnt" }];
      const newSpreadsheetRecordData: EdhPrtclInfo[] =
        mappingErrorResponseToSpreadsheet(
          updateComponentAttributeTicketStatusesResponse,
          currentEdhPrtcl,
          emptyColumnList,
        );
      setSpreadsheetState({
        ...spreadsheetState,
        edhPrtclInfo: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetComponentRequest({
        dipByRgn: dipByRgn,
        rootComponentId: rootCpntId,
        componentId: cpntId,
        godFlg: InDefCommonFlagType.NO,
      });
      setPageState((prevState) => ({
        ...prevState,
        isSaved: true,
      }));
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(onGetComponentResponse, [getComponentResponse]);
  useEffect(onUpdateComponentAttributeResponse, [
    updateComponentAttributesResponse,
  ]);
  useEffect(onUpdateComponentAttributeTicketStatusesResponse, [
    updateComponentAttributeTicketStatusesResponse,
  ]);

  const onCellEdited = (event: CustomEvent) => {
    const eventName: string = event.detail.name;
    const value = event.detail.cell.value;
    const rowIndex = event.detail.value.idx as number;
    const mergeRowIndex = event.detail.cell.data.index as number;
    const spreadsheetData = JSON.parse(JSON.stringify(spreadsheetState));
    const edhPrtclInfo = spreadsheetData.edhPrtclInfo;
    const copyEditCells = spreadsheetData?.editCell;
    let isSaved = pageState.isSaved;
    if (eventName === EVENT_SELECTED_RQUEST) {
      const isSelected = JSON.parse(value.toLowerCase());
      edhPrtclInfo[rowIndex][EVENT_SELECTED_RQUEST] = isSelected;
    } else if (eventName === EVENT_COMMENT) {
      edhPrtclInfo[rowIndex]["edhPrtclAttributeList"][EVENT_COMMENT] = value;
      edhPrtclInfo[rowIndex]["isCommentEdit"] = true;
      let key = "comment" + "-" + edhPrtclInfo[rowIndex]["idx"];
      copyEditCells[key].isDirty = event.detail.cell.row.isDirty;
    } else if (eventName === EVENT_ACTION) {
      edhPrtclInfo[rowIndex][EVENT_ACTION] = value;
      let key = "action" + "-" + edhPrtclInfo[rowIndex]["idx"];
      copyEditCells[key].isDirty = event.detail.cell.row.isDirty;
    } else if (eventName === EVENT_STG_DESIGN_VALUE) {
      edhPrtclInfo[rowIndex]["edhPrtclAttributeList"]["edhPrtclAttributeList"][
        mergeRowIndex
      ][EVENT_STG_DESIGN_VALUE] = value;
      const editedAttributeListInfo =
        edhPrtclInfo[rowIndex]["editedAttributeListInfo"];
      const editedInfo = { index: mergeRowIndex, env: EnvTypeCd.STG };
      editedAttributeListInfo.push(editedInfo);
      edhPrtclInfo[rowIndex]["editedAttributeListInfo"] =
        editedAttributeListInfo;
      isSaved = false;
      let key =
        "stgDesignValue" +
        "-" +
        edhPrtclInfo[rowIndex]["idx"] +
        "-" +
        edhPrtclInfo[rowIndex]["edhPrtclAttributeList"][
          "edhPrtclAttributeList"
        ][mergeRowIndex]["edhPrtclAttributenum"];
      copyEditCells[key].isDirty = event.detail.cell.row.isDirty;
    } else if (eventName === EVENT_PRD_DESIGN_VALUE) {
      edhPrtclInfo[rowIndex]["edhPrtclAttributeList"]["edhPrtclAttributeList"][
        mergeRowIndex
      ][EVENT_PRD_DESIGN_VALUE] = value;
      const editedAttributeListInfo =
        edhPrtclInfo[rowIndex]["editedAttributeListInfo"];
      const editedInfo = { index: mergeRowIndex, env: EnvTypeCd.PRD };
      editedAttributeListInfo.push(editedInfo);
      edhPrtclInfo[rowIndex]["editedAttributeListInfo"] =
        editedAttributeListInfo;
      isSaved = false;
      let key =
        "prdDesignValue" +
        "-" +
        edhPrtclInfo[rowIndex]["idx"] +
        "-" +
        edhPrtclInfo[rowIndex]["edhPrtclAttributeList"][
          "edhPrtclAttributeList"
        ][mergeRowIndex]["edhPrtclAttributenum"];
      copyEditCells[key].isDirty = event.detail.cell.row.isDirty;
    }
    spreadsheetData.edhPrtclInfo = edhPrtclInfo;
    spreadsheetData.editCell = copyEditCells;
    setSpreadsheetState(spreadsheetData);
    setPageState((prevState) => ({
      ...prevState,
      isEdited: true,
      isSaved: isSaved,
    }));
  };

  const onCellClicked = (event: CustomEvent) => {
    const eventName: string = event.detail.name;

    if (eventName === EVENT_COMPONENT_PJ) {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts: TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };

      if (!pageState.isEdited) {
        transition(
          `/component-project-list?${stringify(navigationParameters)}`,
        );
      } else {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(
              `/component-project-list?${stringify(navigationParameters)}`,
            );
          },
        });
      }
    }
    if (eventName === EVENT_COMPONENT_PJ_NEW_TAB_ICON) {
      const navigationParameters = {
        dipByRgn: params.dipByRgn,
        cpntId: params.cpntId,
        rootCpntId: params.rootCpntId,
        cpntPjtSts: TcktStsTypeCpntPrjPrgrs.COMPONENT_PROJECT_IN_PROGRESS,
      };
      const href = createPath({
        pathname: `/component-project-list?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const onClickWithdrawReviewRequest = () => {
    const spreadsheetData = JSON.parse(JSON.stringify(spreadsheetState));
    const edhPrtclInfo = spreadsheetData.edhPrtclInfo as EdhPrtclInfo[];
    const targetedhPrtclInfo = edhPrtclInfo.filter((e) => {
      return e.isRequest === true;
    });
    if (targetedhPrtclInfo.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    let invalidDesignStatus = false;
    targetedhPrtclInfo.forEach((e) => {
      if (
        e.edhPrtclAttributeList.designStatus !==
        TcktStsTypeCpntDsgn.DESIGN_REVIEW
      ) {
        invalidDesignStatus = true;
      }
    });
    if (invalidDesignStatus) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00041,
          "withdraw review request",
          "Reviewing",
        ),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(
        ApplicationMessageScreen.APP_SCN_00169,
        "withdraw",
        "review request",
      ),
      onOkButtonClicked: () => {
        resetUserError();
        const updateCpntTcktList = [] as UpdateCpntTckt[];
        targetedhPrtclInfo.forEach((e) => {
          const updateCpntTckt = {} as UpdateCpntTckt;
          updateCpntTckt.idx = e.idx;
          updateCpntTckt.pjtId = e.edhPrtclAttributeList.inProgressCmpProjectId;
          updateCpntTckt.evntGrpTypeCd = EvntGrp.EVNT_GRP_PT_010_004;
          updateCpntTckt.tcktTypeTypeCd = TcktType.COMPONENT_DESIGN;
          updateCpntTckt.tcktStsTypeCd = TcktStsTypeCpntDsgn.DESIGN;
          updateCpntTckt.cmnt = e.edhPrtclAttributeList.cmnt;
          updateCpntTcktList.push(updateCpntTckt);
        });
        sendUpdateComponentAttributeTicketStatusesRequest({
          updateCpntTcktList,
        });
      },
    });
  };

  const onClickRequestReview = () => {
    const spreadsheetData = JSON.parse(JSON.stringify(spreadsheetState));
    const edhPrtclInfo = spreadsheetData.edhPrtclInfo as EdhPrtclInfo[];
    const targetedhPrtclInfo = edhPrtclInfo.filter((e) => {
      return e.isRequest === true;
    });
    if (targetedhPrtclInfo.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    let invalidDesignStatus = false;
    targetedhPrtclInfo.forEach((e) => {
      if (e.edhPrtclAttributeList.designStatus !== TcktStsTypeCpntDsgn.DESIGN) {
        invalidDesignStatus = true;
      }
    });
    if (invalidDesignStatus) {
      showToast({
        design: "alert",
        message: messageForScreen(
          ApplicationMessageScreen.APP_SCN_00041,
          "request review",
          "Designing",
        ),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(
        ApplicationMessageScreen.APP_SCN_00169,
        "request",
        "review",
      ),
      onOkButtonClicked: () => {
        resetUserError();
        const updateCpntTcktList = [] as UpdateCpntTckt[];
        targetedhPrtclInfo.forEach((e) => {
          const updateCpntTckt = {} as UpdateCpntTckt;
          updateCpntTckt.idx = e.idx;
          updateCpntTckt.pjtId = e.edhPrtclAttributeList.inProgressCmpProjectId;
          updateCpntTckt.evntGrpTypeCd = EvntGrp.EVNT_GRP_PT_010_001;
          updateCpntTckt.tcktTypeTypeCd = TcktType.COMPONENT_DESIGN;
          updateCpntTckt.tcktStsTypeCd = TcktStsTypeCpntDsgn.DESIGN_REVIEW;
          updateCpntTckt.cmnt = e.edhPrtclAttributeList.cmnt;
          updateCpntTcktList.push(updateCpntTckt);
        });
        sendUpdateComponentAttributeTicketStatusesRequest({
          updateCpntTcktList,
        });
      },
    });
  };

  const onClickSave = () => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        resetUserError();
        const edhPrtclInfoList = [] as CpntAttr[];
        const spreadsheetData: SpreadsheetState = JSON.parse(
          JSON.stringify(spreadsheetState),
        );
        const pageEdhPrtclInfo = spreadsheetData.edhPrtclInfo;
        pageEdhPrtclInfo.forEach((edhPrtcl) => {
          if (edhPrtcl.editedAttributeListInfo.length === 0) return;
          edhPrtcl.editedAttributeListInfo.forEach((editInfo) => {
            let edhPrtclInfo: CpntAttr = {
              cpntPjtId: edhPrtcl.edhPrtclAttributeList.inProgressCmpProjectId,
              index: edhPrtcl.idx,
              cpntAttrNum:
                edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList[
                  editInfo.index
                ].edhPrtclAttributenum,
              envTypeCd: editInfo.env,
              stngVal:
                editInfo.env === EnvTypeCd.STG
                  ? edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList[
                      editInfo.index
                    ].stgDesignValue
                  : edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList[
                      editInfo.index
                    ].prdDesignValue,
              updateDateTime:
                editInfo.env === EnvTypeCd.STG
                  ? edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList[
                      editInfo.index
                    ].updateDateTimeStgDesignValue
                  : edhPrtcl.edhPrtclAttributeList.edhPrtclAttributeList[
                      editInfo.index
                    ].updateDateTimePrdDesignValue,
            };
            edhPrtclInfoList.push(edhPrtclInfo);
          });
        });
        const deduplicatedCpntAttrList = edhPrtclInfoList.filter(
          (e, index, self) =>
            self.findIndex(
              (f) =>
                f.envTypeCd === e.envTypeCd && f.cpntAttrNum === e.cpntAttrNum,
            ) === index,
        );
        sendUpdateComponentAttributesRequest({
          cpntId: cpntId,
          dipByRgn: dipByRgn,
          godFlg: params.godFlg,
          cpntAttrList: deduplicatedCpntAttrList,
        });
      },
    });
  };

  const onClickCompleteReview = () => {
    const updateComponentAttributeTicketStatuses = () => {
      resetUserError();
      const updateCpntTcktList = [] as UpdateCpntTckt[];
      targetedhPrtclInfo.forEach((e) => {
        const updateCpntTckt = {} as UpdateCpntTckt;
        const evntGrpTypeCd =
          e.action === ReviewActionType.APPROVE
            ? EvntGrp.EVNT_GRP_PT_010_002
            : e.action === ReviewActionType.REJECT
              ? EvntGrp.EVNT_GRP_PT_010_003
              : EvntGrp.EVNT_GRP_PT_010_005;
        const tcktStsTypeCd =
          e.action === ReviewActionType.APPROVE
            ? TcktStsTypeCpntDsgn.DESIGN_APPROVED
            : TcktStsTypeCpntDsgn.DESIGN;
        updateCpntTckt.idx = e.idx;
        updateCpntTckt.pjtId = e.edhPrtclAttributeList.inProgressCmpProjectId;
        updateCpntTckt.evntGrpTypeCd = evntGrpTypeCd;
        updateCpntTckt.tcktTypeTypeCd = TcktType.COMPONENT_DESIGN;
        updateCpntTckt.tcktStsTypeCd = tcktStsTypeCd;
        updateCpntTckt.cmnt = e.edhPrtclAttributeList.cmnt;
        updateCpntTcktList.push(updateCpntTckt);
      });
      sendUpdateComponentAttributeTicketStatusesRequest({
        updateCpntTcktList,
      });
    };
    const spreadsheetData = JSON.parse(
      JSON.stringify(spreadsheetState?.edhPrtclInfo),
    );
    const changedActionRow = spreadsheetData as EdhPrtclInfo[];
    const targetedhPrtclInfo = changedActionRow.filter((e) => {
      return e.action !== "";
    });
    if (targetedhPrtclInfo.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    const discardCommentRow = changedActionRow.filter((e) => {
      return e.action === "" && e.isCommentEdit;
    });
    if (discardCommentRow.length === 0) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00034),
        onOkButtonClicked: () => {
          updateComponentAttributeTicketStatuses();
        },
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(
        ApplicationMessageScreen.APP_SCN_00169,
        "complete",
        "review",
      ),
      onOkButtonClicked: () => {
        updateComponentAttributeTicketStatuses();
        return;
      },
    });
  };

  const onClickBack = () => {
    if (!pageState.isEdited) {
      navigate(-1);
    } else {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          navigate(-1);
        },
      });
    }
  };

  const onClickReload = () => {
    if (pageState.isEdited) {
      window.location.reload();
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        window.location.reload();
        return;
      },
    });
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newEdhProtocolList = _.cloneDeep(
      spreadsheetState?.edhPrtclInfo,
    ) as EdhPrtclInfo[];
    const value: string = event.detail.cell.value;

    const dataNum = newEdhProtocolList.map(
      (item, index) =>
        item.edhPrtclAttributeList.edhPrtclAttributeList.length || 1,
    );
    const editCells = spreadsheetState?.editCell as EditCell;

    let nextParentIdx = 0;
    let isSaved = pageState.isSaved;
    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const getIdxs = (rowIdx: number) => {
        let parentIdx = 0;
        let rowSums = 0;
        for (; parentIdx < dataNum.length; parentIdx++) {
          if (rowSums + dataNum[parentIdx] > rowIdx) break;
          rowSums += dataNum[parentIdx];
        }
        return { parentIdx, rowSums };
      };
      const rowIdx: number = item.row.index - 1;
      // there is used union cells in spreadsheet, so need to calculate parent index
      const parentIdx = rowIdx > 0 ? getIdxs(rowIdx).parentIdx : 0;

      if (nextParentIdx > parentIdx) return; // skip if same parent index

      if (event.detail.name === EVENT_SELECTED_RQUEST) {
        newEdhProtocolList[parentIdx].isRequest = value === "true";
        nextParentIdx++;
      } else if (event.detail.name === EVENT_ACTION) {
        newEdhProtocolList[parentIdx].action = value;
        nextParentIdx++;
        editCells[
          event.detail.name + "-" + newEdhProtocolList[parentIdx]["idx"]
        ].isDirty = event.detail.cell.row.isDirty;
      } else if (event.detail.name === EVENT_COMMENT) {
        newEdhProtocolList[parentIdx].edhPrtclAttributeList.cmnt = value;
        newEdhProtocolList[parentIdx].isCommentEdit = true;
        editCells[
          "comment" + "-" + newEdhProtocolList[parentIdx]["idx"]
        ].isDirty = event.detail.cell.row.isDirty;
        nextParentIdx++;
      } else {
        const edhPrtclAttribute =
          newEdhProtocolList[parentIdx].edhPrtclAttributeList
            .edhPrtclAttributeList[rowIdx - getIdxs(rowIdx).rowSums];
        event.detail.name === EVENT_STG_DESIGN_VALUE
          ? (edhPrtclAttribute.stgDesignValue = value)
          : (edhPrtclAttribute.prdDesignValue = value);
        const editCell =
          editCells[
            event.detail.name +
              "-" +
              newEdhProtocolList[parentIdx]["idx"] +
              "-" +
              edhPrtclAttribute.edhPrtclAttributenum
          ];
        editCell.isDirty = event.detail.cell.row.isDirty;
        editCell.initialValue = value;
        const editedAttributeListInfo =
          newEdhProtocolList[parentIdx]["editedAttributeListInfo"];
        const mergeRowIndex = rowIdx - getIdxs(rowIdx).rowSums;
        const editedInfo = { index: mergeRowIndex, env: EnvTypeCd.STG };
        editedAttributeListInfo.push(editedInfo);
        isSaved = false;
      }
    });

    setPageState({
      ...pageState,
      isEdited: true,
      isSaved: isSaved,
    });

    setSpreadsheetState({
      ...spreadsheetState,
      editCell: editCells,
      edhPrtclInfo: newEdhProtocolList,
    });
  };

  return (
    <>
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={templateFileUrl}
        onInitialized={initSpreadsheetState}
        onCellEdited={onCellEdited}
        onCellClicked={onCellClicked}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />
      <FrVrfFooter>
        {mode === PAGE_MODE.REQUEST_APPROVAL && (
          <>
            <FrVrfButton
              content="Back"
              theme="secondary"
              size="large"
              slot="left"
              onClicked={onClickBack}
            />
            <FrVrfButton
              content="Withdraw Review Request"
              theme="primary"
              size="large"
              onClicked={onClickWithdrawReviewRequest}
              authActId="edh_protocol_attribute_request_approval.button.withdraw_review_request"
            />
            <FrVrfButton
              content="Request Review"
              theme="primary"
              size="large"
              onClicked={onClickRequestReview}
              authActId="edh_protocol_attribute_request_approval.button.request_review"
            />
          </>
        )}
        {mode === PAGE_MODE.REVIEW && (
          <>
            <FrVrfButton
              content="Back"
              theme="secondary"
              size="large"
              slot="left"
              onClicked={onClickBack}
            />
            <FrVrfButton
              content="Reload"
              theme="secondary"
              size="large"
              onClicked={onClickReload}
              authActId="edh_protocol_attribute_review.button.reload"
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              disabled={pageState.isSaved}
              onClicked={onClickSave}
              authActId="edh_protocol_attribute_review.button.save"
            />
            <FrVrfButton
              content="Complete Review"
              theme="primary"
              size="large"
              disabled={
                !pageState.isSaved || (!pageState.isEdited && pageState.isSaved)
              }
              onClicked={onClickCompleteReview}
              authActId="edh_protocol_attribute_review.button.complete_review"
            />
          </>
        )}
      </FrVrfFooter>
    </>
  );
};
